import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Utensils, Shield, MapPin, Clock, Briefcase, GraduationCap, Wrench } from 'lucide-react';
import { useLanguageStore } from '../store/languageStore';
import { useMatchStore } from '../store/matchStore';
import MatchBadge from './MatchBadge';

interface ResourceCardProps {
  id: number;
  type: 'rent' | 'food' | 'shelter' | 'legal' | 'jobs';
  name: string;
  description: string;
  matchPercentage: number;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ 
  id, 
  type, 
  name, 
  description,
  matchPercentage
}) => {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguageStore();
  const { matchResults } = useMatchStore();

  // Get the AI program data for this specific result
  const matchResult = matchResults.find(result => result.id === id);
  const aiProgram = matchResult?.aiProgram;

  const messages = {
    EN: {
      viewDetails: 'View Details',
      openNow: 'Open Now',
      zip: 'ZIP',
      distance: 'miles away'
    },
    ES: {
      viewDetails: 'Ver Detalles',
      openNow: 'Abierto Ahora',
      zip: 'Código ZIP',
      distance: 'millas de distancia'
    },
    ZH: {
      viewDetails: '查看详情',
      openNow: '现在营业',
      zip: '邮编',
      distance: '英里远'
    },
    VI: {
      viewDetails: 'Xem Chi Tiết',
      openNow: 'Đang Mở Cửa',
      zip: 'Mã ZIP',
      distance: 'dặm'
    },
    FIL: {
      viewDetails: 'Tingnan ang Detalye',
      openNow: 'Bukas Ngayon',
      zip: 'ZIP Code',
      distance: 'milya ang layo'
    },
    KO: {
      viewDetails: '상세 보기',
      openNow: '현재 영업중',
      zip: '우편번호',
      distance: '마일 거리'
    },
    AR: {
      viewDetails: 'عرض التفاصيل',
      openNow: 'مفتوح الآن',
      zip: 'الرمز البريدي',
      distance: 'أميال'
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'rent':
      case 'shelter':
        return <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
      case 'food':
        return <Utensils className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />;
      case 'legal':
        return <Shield className="w-5 h-5 text-violet-600 dark:text-violet-400" />;
      case 'jobs':
        if (name.includes('Training')) {
          return <GraduationCap className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
        } else if (name.includes('Career')) {
          return <Briefcase className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
        } else {
          return <Wrench className="w-5 h-5 text-amber-600 dark:text-amber-400" />;
        }
      default:
        return <Home className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getCardAccent = () => {
    switch (type) {
      case 'rent':
      case 'shelter':
        return 'border-l-4 border-blue-400/70 dark:border-blue-500/40 bg-blue-50/30 dark:bg-blue-900/10';
      case 'food':
        return 'border-l-4 border-emerald-400/70 dark:border-emerald-500/40 bg-emerald-50/30 dark:bg-emerald-900/10';
      case 'legal':
        return 'border-l-4 border-violet-400/70 dark:border-violet-500/40 bg-violet-50/30 dark:bg-violet-900/10';
      case 'jobs':
        return 'border-l-4 border-amber-400/70 dark:border-amber-500/40 bg-amber-50/30 dark:bg-amber-900/10';
      default:
        return 'border-l-4 border-blue-400/70 dark:border-blue-500/40 bg-blue-50/30 dark:bg-blue-900/10';
    }
  };

  // Get the actual zip code from AI program data
  const actualZipCode = aiProgram?.address?.zip || 'N/A';
  const distance = aiProgram?.distance ? `${aiProgram.distance.toFixed(1)} ${messages[selectedLanguage].distance}` : '';

  return (
    <div 
      className={`resource-card p-4 sm:p-6 ${getCardAccent()} hover:shadow-lg transition-all duration-300 cursor-pointer`}
      onClick={() => navigate(`/details?resourceId=${id}`)}
    >
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <div className="resource-icon p-2 sm:p-3 self-start">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
            <MatchBadge percentage={matchPercentage} />
            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
          </div>
          
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
            {description}
          </p>
          
          <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="truncate">{messages[selectedLanguage].zip} {actualZipCode}</span>
            </div>
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="truncate">{distance || messages[selectedLanguage].openNow}</span>
            </div>
          </div>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/details?resourceId=${id}`);
            }}
            className="btn-secondary text-xs sm:text-sm mt-4 py-1.5 px-3 sm:py-2 sm:px-4"
          >
            {messages[selectedLanguage].viewDetails}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;